/**
 * SkillPermissionsSelector
 *
 * Compact, searchable skill picker for the workflow AI node editor.
 * Lets admins restrict which AI skills a specific workflow node may use,
 * independently of the site-wide subscription and override settings.
 *
 * Behaviour:
 *   - Empty selection = "All enabled skills" (default, no restriction).
 *   - Non-empty selection = only those skills are offered to the AI at runtime.
 *   - The filter is applied AFTER subscription gate and skill override checks —
 *     the most restrictive setting always wins.
 */

import React, { useState } from "react";
import { SkillDef } from "./SkillControlsPanel";

interface Props {
  /** All skills for the site's enabled integrations (passed from parent). */
  availableSkills: SkillDef[];
  /** Currently selected tool names. Empty array = all skills permitted. */
  permittedSkills: string[];
  /** Called when the selection changes. */
  onChange: (skills: string[]) => void;
}

export default function SkillPermissionsSelector({
  availableSkills,
  permittedSkills,
  onChange,
}: Props) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const allSelected = permittedSkills.length === 0;

  const isAllowed = (name: string) =>
    allSelected || permittedSkills.includes(name);

  const filtered = availableSkills.filter(
    (s) =>
      !search ||
      s.label.toLowerCase().includes(search.toLowerCase()) ||
      s.name.toLowerCase().includes(search.toLowerCase())
  );

  const groups = Array.from(new Set(filtered.map((s) => s.group ?? "General")));

  function toggle(name: string) {
    if (allSelected) {
      // Switching from "all" to explicit — start with everything except this one
      onChange(availableSkills.map((s) => s.name).filter((n) => n !== name));
    } else if (permittedSkills.includes(name)) {
      const next = permittedSkills.filter((n) => n !== name);
      onChange(next); // empty = back to "all"
    } else {
      onChange([...permittedSkills, name]);
    }
  }

  function resetToAll() {
    onChange([]);
    setSearch("");
  }

  function selectAll() {
    onChange(filtered.map((s) => s.name));
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-700">
          Permitted Skills
        </label>
        {!allSelected && (
          <button
            type="button"
            onClick={resetToAll}
            className="text-xs text-blue-600 hover:underline"
          >
            Allow all
          </button>
        )}
      </div>

      {allSelected ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => e.key === "Enter" && setOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 cursor-pointer hover:bg-slate-100 text-sm text-slate-500 select-none"
        >
          <span className="text-green-500">✓</span>
          <span>All enabled skills — click to restrict</span>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => e.key === "Enter" && setOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 cursor-pointer hover:bg-amber-100 text-sm select-none"
        >
          <span className="font-semibold text-amber-800">
            {permittedSkills.length}{" "}
            {permittedSkills.length === 1 ? "skill" : "skills"} permitted
          </span>
          <span className="text-amber-600 text-xs">click to edit</span>
        </div>
      )}

      {open && (
        <div className="rounded-lg border border-slate-200 overflow-hidden shadow-sm">
          {/* Search + bulk actions */}
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <input
              type="text"
              placeholder="Search skills…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 text-xs px-3 py-1.5 rounded border border-slate-200 focus:outline-none focus:border-blue-400"
              autoFocus
            />
            {!allSelected && (
              <button
                type="button"
                onClick={selectAll}
                className="text-xs text-slate-500 hover:text-blue-600 whitespace-nowrap"
                title="Select all visible skills"
              >
                Select all
              </button>
            )}
          </div>

          {/* Skill list grouped by integration */}
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {groups.length === 0 && (
              <div className="px-4 py-3 text-xs text-slate-400">
                No skills match your search.
              </div>
            )}
            {groups.map((group) => (
              <div key={group}>
                <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  {group}
                </div>
                {filtered
                  .filter((s) => (s.group ?? "General") === group)
                  .map((skill) => {
                    const checked = isAllowed(skill.name);
                    return (
                      <div key={skill.name}>
                        <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(skill.name)}
                            className="rounded border-slate-300 text-blue-600"
                          />
                          <span className="text-xs text-slate-700 font-medium">
                            {skill.label}
                          </span>
                          {skill.destructive && (
                            <span className="text-[9px] font-bold uppercase bg-red-100 text-red-600 px-1 rounded ml-1">
                              Destructive
                            </span>
                          )}
                          <span className="text-[10px] font-mono text-slate-300 ml-auto truncate">
                            {skill.name}
                          </span>
                        </label>
                        {checked && skill.channelWarning && (
                          <div className="mx-3 mb-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800 leading-relaxed">
                            <span className="font-semibold">IVR/SMS note: </span>
                            {skill.channelWarning}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>

          {/* Footer: close button */}
          <div className="p-2 border-t border-slate-100 flex justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {!allSelected && (
        <p className="text-[10px] text-amber-700">
          Only the {permittedSkills.length} selected{" "}
          {permittedSkills.length === 1 ? "skill" : "skills"} will be available
          to this AI node at runtime, even if more skills are enabled site-wide.
        </p>
      )}
    </div>
  );
}
